// Waterfall drill-down drawer — the full plain-language dossier for one
// priority row of a WaterfallConfig, served by
// GET /portfolio-admin/waterfalls/configs/{configId}/priorities/{position}/detail.
// Rides the shared ResourceDrawer (right-edge panel) like the loan-detail
// tabs; the "Explain" footer action opens the AI coach modal.

import { useState } from "react";
import { Badge, Button } from "@/design-system/components/ui";
import { ResourceDrawer } from "@/components/portfolioAdmin/loanDetail/common/ResourceDrawer";
import { WaterfallExplainModal } from "./WaterfallExplainModal";
import { useWaterfallPriorityDetail } from "@/hooks/portfolioAdmin/usePr6";
import { TRIGGER_CODE_DESCRIPTIONS } from "./triggerCodes";
import type { WaterfallPriorityDetailDto } from "@/api/portfolioAdmin/pr6Types";

interface PriorityDetailDrawerProps {
  configId: string;
  position: number;
  onClose: () => void;
  /** True when the parent editor holds unsaved draft edits — the detail
   *  endpoint reads the saved config, so the drawer flags the gap. */
  hasUnsavedEdits?: boolean;
}

export function PriorityDetailDrawer({
  configId,
  position,
  onClose,
  hasUnsavedEdits = false,
}: PriorityDetailDrawerProps) {
  const query = useWaterfallPriorityDetail(configId, position, true);
  const detail = query.data ?? null;
  const [explainOpen, setExplainOpen] = useState(false);

  return (
    <ResourceDrawer
      open
      onClose={onClose}
      title={
        detail
          ? `#${detail.priorityPosition} — ${detail.obligationDisplayName ?? detail.obligationTypeCode}`
          : `Priority #${position}`
      }
      subtitle={
        detail
          ? `${detail.waterfallCode} v${detail.configVersion} · ${detail.configStatus}`
          : undefined
      }
      width="lg"
      testId="priority-detail-drawer"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button
            type="button"
            disabled={!detail}
            onClick={() => setExplainOpen(true)}
            data-testid="priority-detail-explain-button"
          >
            Explain
          </Button>
        </>
      }
    >
      {hasUnsavedEdits && (
        <p
          className="text-caption text-gray-mid italic m-0 mb-space-3"
          data-testid="priority-detail-unsaved-note"
        >
          Details reflect the last-saved version.
        </p>
      )}

      {query.isLoading && (
        <p className="text-small text-gray-mid m-0">Loading priority details…</p>
      )}
      {query.isError && (
        <p role="alert" className="text-small text-status-error m-0">
          Couldn't load this priority's details.
        </p>
      )}

      {detail && <DetailBody detail={detail} />}

      {detail && explainOpen && (
        <WaterfallExplainModal
          configId={configId}
          position={position}
          priorityLabel={detail.obligationDisplayName ?? detail.obligationTypeCode}
          onClose={() => setExplainOpen(false)}
        />
      )}
    </ResourceDrawer>
  );
}

function DetailBody({ detail }: { detail: WaterfallPriorityDetailDto }) {
  return (
    <div className="flex flex-col gap-space-4">
      {/* ------------------------------ Identity ------------------------ */}
      <Section title="Identity">
        <Field label="Obligation">
          {detail.obligationDisplayName ?? detail.obligationTypeCode}{" "}
          <Code>{detail.obligationTypeCode}</Code>
        </Field>
        {detail.planningDocReference && (
          <Field label="Planning doc">{detail.planningDocReference}</Field>
        )}
        {detail.obligationDescription && (
          <p className="text-body text-onyx m-0">
            {detail.obligationDescription}
          </p>
        )}
        <Field label="Created">{detail.obligationCreationMode}</Field>
      </Section>

      {/* ----------------------------- Money math ----------------------- */}
      <Section title="Money math">
        <Field label="Formula">
          {detail.formulaDisplayName ?? detail.amountFormulaCode}{" "}
          <Code>{detail.amountFormulaCode}</Code>{" "}
          <span className="text-caption text-gray-mid">
            ({detail.formulaType ?? "unknown type"})
          </span>
        </Field>
        {detail.calculationSource !== "formula" && (
          <div
            className="rounded-md border border-border-light bg-white-warm p-space-3 flex flex-col gap-space-1"
            data-testid="priority-detail-calc-source"
          >
            <Badge status="info" className="self-start">
              {detail.calculationSource === "orchestrator_injected"
                ? "Orchestrator-injected"
                : "Caller-supplied"}
            </Badge>
            <p className="text-small text-onyx m-0">
              {detail.calculationSource === "orchestrator_injected"
                ? "Real calculation happens in the servicing engine — this formula receives the result."
                : "The triggering event supplies this amount."}
            </p>
            {detail.calculationSourceDetail && (
              <p className="text-caption text-gray-mid m-0">
                {detail.calculationSourceDetail}
              </p>
            )}
          </div>
        )}
        {detail.formulaParametersJson && (
          <Field label="Parameters">
            <pre className="m-0 p-space-2 bg-white-warm border border-border-light rounded-md text-caption font-mono whitespace-pre-wrap">
              {prettyJson(detail.formulaParametersJson)}
            </pre>
          </Field>
        )}
        {detail.workedExample && (
          <Field label="Worked example">
            <pre
              className="m-0 p-space-2 bg-white-warm border border-border-light rounded-md text-small whitespace-pre-wrap font-body"
              data-testid="priority-detail-worked-example"
            >
              {detail.workedExample}
            </pre>
          </Field>
        )}
      </Section>

      {/* ---------------------------- Who gets paid --------------------- */}
      <Section title="Who gets paid">
        <Field label="Recipient">
          <Code>{detail.recipientResolution}</Code>
          {detail.recipientResolverIsStub && <StubBadge />}
          {detail.recipientResolverDescription && (
            <p className="text-small text-gray-mid m-0 mt-space-1">
              {detail.recipientResolverDescription}
            </p>
          )}
        </Field>
        <Field label="Account">
          <Code>{detail.accountResolution}</Code>
          {detail.accountResolverIsStub && <StubBadge />}
          {detail.accountResolverDescription && (
            <p className="text-small text-gray-mid m-0 mt-space-1">
              {detail.accountResolverDescription}
            </p>
          )}
        </Field>
        <Field label="Source account">
          <Code>{detail.sourceAccountResolution}</Code>{" "}
          <Badge
            status={detail.settlementChannel === "phoenix" ? "gold" : "info"}
            data-testid="priority-detail-settlement-channel"
          >
            {detail.settlementChannel}
          </Badge>
          {detail.settlementChannelNote && (
            <p className="text-small text-gray-mid m-0 mt-space-1">
              {detail.settlementChannelNote}
            </p>
          )}
        </Field>
      </Section>

      {/* ------------------------------ Triggers ------------------------ */}
      <Section title="Triggers">
        {!detail.isCurrentlyFireable && (
          <div
            className="rounded-md border border-status-warning bg-status-warning/10 p-space-3 text-small text-onyx"
            role="alert"
            data-testid="priority-detail-unfireable-banner"
          >
            No system event currently triggers this priority — it cannot pay
            out today.
          </div>
        )}
        <div className="flex flex-wrap gap-space-2">
          {detail.firesOn.map((t) => {
            const label = t.description ?? TRIGGER_CODE_DESCRIPTIONS[t.code];
            return (
              <div key={t.code} className="flex flex-col gap-space-1">
                <span
                  title={label}
                  className={
                    "inline-flex items-center px-space-2 h-7 border rounded-md text-caption font-mono " +
                    (t.isEmitted
                      ? "border-gold bg-gold-highlight/10 text-onyx"
                      : "border-border-light bg-white-pure text-gray-mid opacity-70")
                  }
                  data-testid={`priority-detail-trigger-${t.code}`}
                >
                  {t.code}
                </span>
                {label && (
                  <span
                    className="text-caption text-gray-mid"
                    data-testid={`priority-detail-trigger-label-${t.code}`}
                  >
                    {label}
                  </span>
                )}
              </div>
            );
          })}
          {detail.firesOn.length === 0 && (
            <p className="text-small text-gray-mid italic m-0">
              This priority fires on no trigger codes.
            </p>
          )}
        </div>
      </Section>

      {/* ------------------------------- Flags -------------------------- */}
      <Section title="Flags">
        <Field label="Take all remaining">
          {detail.isTakeAllRemaining ? "Yes" : "No"}
        </Field>
        <Field label="Persistent">{detail.isPersistent ? "Yes" : "No"}</Field>
        {detail.conditionalLogicJson && (
          <Field label="Conditional logic">
            <pre className="m-0 p-space-2 bg-white-warm border border-border-light rounded-md text-caption font-mono whitespace-pre-wrap">
              {prettyJson(detail.conditionalLogicJson)}
            </pre>
          </Field>
        )}
      </Section>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-space-2">
      <h3 className="font-display font-bold text-h4 text-onyx m-0">{title}</h3>
      {children}
    </section>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-space-1">
      <span className="text-caption uppercase tracking-wide text-gray-mid">
        {label}
      </span>
      <div className="text-body text-onyx">{children}</div>
    </div>
  );
}

function Code({ children }: { children: React.ReactNode }) {
  return (
    <span className="font-mono text-small bg-white-warm border border-border-light rounded-sm px-space-1">
      {children}
    </span>
  );
}

function StubBadge() {
  return (
    <Badge
      status="error"
      className="ml-space-2"
      data-testid="priority-detail-stub-badge"
    >
      Stub — resolves to nobody; this line never pays
    </Badge>
  );
}

function prettyJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}
